import apiClient from './index';

export type FeedbackItemStatus = 'pending' | 'resolved';

export type FeedbackBatchStatus = 'draft' | 'submitted' | 'resolved';

export interface FeedbackItemDetail {
  id: number;
  batchId: number;
  questionId: number | null;
  documentId: number | null;
  feedbackText: string;
  orderIndex: number;
  status: FeedbackItemStatus;
  resolvedAt: string | null;
  adminId: string | null;
  adminEmail: string | null;
  createdAt: string | null;
  questionContext: { id: number; text: string; formId: number } | null;
  documentContext: {
    id: number;
    fileName: string;
    documentCategory: string | null;
  } | null;
}

export interface FeedbackBatchDetail {
  id: number;
  applicationId: number;
  applicationFormId: number;
  formId: number | null;
  formName: string | null;
  status: FeedbackBatchStatus;
  submittedAt: string | null;
  submittedByAdminId: string | null;
  submittedByAdminEmail: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  application: {
    id: number;
    caseNumber: string;
    beneficiaryName: string;
    visaType: string;
    stage?: string;
  } | null;
  user: { id: number; name: string; email: string } | null;
  items: FeedbackItemDetail[];
}

export interface FeedbackBatchesListResponse {
  success: boolean;
  data: {
    batches: FeedbackBatchDetail[];
    pagination: { total: number; limit: number; offset: number };
  };
}

export interface FeedbackBatchResponse {
  success: boolean;
  data: FeedbackBatchDetail | null;
  meta?: { applicationFormId: number };
}

export const getUserFeedbackBatches = async (
  applicationId: number,
  params?: { formId?: number }
): Promise<{ batches: FeedbackBatchDetail[]; pagination: { total: number; limit: number; offset: number } }> => {
  const searchParams = new URLSearchParams();
  if (params?.formId != null) searchParams.set('formId', String(params.formId));
  const query = searchParams.toString();
  const response = await apiClient.get<FeedbackBatchesListResponse>(
    `/applications/${applicationId}/feedback${query ? `?${query}` : ''}`
  );
  return response.data.data;
};

export const getUserActiveFeedbackBatch = async (
  applicationId: number,
  formId: number
): Promise<FeedbackBatchDetail | null> => {
  const response = await apiClient.get<FeedbackBatchResponse>(
    `/applications/${applicationId}/forms/${formId}/feedback-batch`
  );
  return response.data.data;
};

export const resolveFeedbackItem = async (
  applicationId: number,
  itemId: number
): Promise<FeedbackBatchDetail> => {
  const response = await apiClient.patch<{ success: boolean; data: FeedbackBatchDetail }>(
    `/applications/${applicationId}/feedback-items/${itemId}/resolve`
  );
  return response.data.data;
};

export const resubmitFormAfterFeedback = async (
  applicationId: number,
  formId: number
): Promise<{ application: { id: number; stage: string }; batch: FeedbackBatchDetail }> => {
  const response = await apiClient.post<{
    success: boolean;
    data: { application: { id: number; stage: string }; batch: FeedbackBatchDetail };
  }>(`/applications/${applicationId}/forms/${formId}/feedback-resubmit`);
  return response.data.data;
};

/** Only callable while the feedback batch is still submitted; the backend enforces this constraint. */
export const unresolveFeedbackItem = async (
  applicationId: number,
  itemId: number
): Promise<FeedbackBatchDetail> => {
  const response = await apiClient.patch<{ success: boolean; data: FeedbackBatchDetail }>(
    `/applications/${applicationId}/feedback-items/${itemId}/unresolve`
  );
  return response.data.data;
};
